import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { identifyUser, resetUser } from '../lib/analytics';
import { setSentryUser, clearSentryUser } from '../lib/sentry';

/**
 * Authentication State Store
 * 
 * Manages authentication state including user data and JWT tokens.
 * Implements Requirements 4.1, 4.2, 4.3, 4.6, 4.7, 19.6:
 * - Zustand for global state management
 * - TypeScript-typed store with full type inference
 * - Middleware for persistence and devtools
 * - JWT token management with localStorage persistence
 * - Integration with Redux DevTools for debugging
 * - User context for error monitoring and analytics
 * 
 * SECURITY NOTES:
 * - Access tokens stored in localStorage for development/testing
 * - In production, consider using httpOnly cookies for enhanced security
 * - Refresh tokens persisted to localStorage for session persistence
 * - Tokens automatically included in Authorization header
 * - Token refresh handled automatically on expiration
 */

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  company_id: string;
  role?: string; // Primary role name (e.g. 'super_admin', 'admin', 'viewer')
  scope?: 'global' | 'company' | 'none'; // Role scope — global = super_admin
  permissions?: string[]; // Explicit permissions (optional, can be derived from role)
}

export interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setUser: (user: User | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  clearError: () => void;
}

/**
 * Authentication Store with persistence and devtools middleware
 * 
 * SECURITY NOTE: Persists refresh token for session management
 * Access token kept in memory only for security
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,

        // Actions
        setUser: (user) => 
          set({ user }, false, 'setUser'),

        setTokens: (accessToken, refreshToken) =>
          set({ accessToken, refreshToken }, false, 'setTokens'),

        setAuthenticated: (isAuthenticated) =>
          set({ isAuthenticated }, false, 'setAuthenticated'),

        setLoading: (isLoading) =>
          set({ isLoading }, false, 'setLoading'),

        setError: (error) =>
          set({ error }, false, 'setError'),

        clearError: () =>
          set({ error: null }, false, 'clearError'),

        /**
         * Login user with email and password
         * 
         * Backend returns JWT tokens in response body
         * Frontend stores tokens and user data
         */
        login: async (email: string, password: string) => {
          set({ isLoading: true, error: null }, false, 'login:start');
          
          try {
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v3';
            const response = await fetch(`${apiBaseUrl}/auth/login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error?.message || 'Login failed');
            }

            const data = await response.json();
            
            // Backend returns: { success: true, data: { access_token, refresh_token, user } }
            const user = data.data.user;
            
            // Set user context for error monitoring and analytics (Requirement 19.6)
            setSentryUser({
              id: user.id,
              email: user.email,
              name: `${user.first_name} ${user.last_name}`,
            });
            
            identifyUser(user.id, {
              email: user.email,
              name: `${user.first_name} ${user.last_name}`,
              company_id: user.company_id,
            });
            
            set(
              {
                user,
                accessToken: data.data.access_token,
                refreshToken: data.data.refresh_token,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              },
              false,
              'login:success'
            );
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Login failed';
            set(
              {
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
                error: errorMessage,
              },
              false,
              'login:error'
            );
            throw error;
          }
        },

        /**
         * Logout user
         * 
         * Clears tokens and user data
         */
        logout: async () => {
          set({ isLoading: true, error: null }, false, 'logout:start');
          
          try {
            // Optional: Call backend logout endpoint if it exists
            // await fetch('/api/v3/auth/logout', {
            //   method: 'POST',
            //   headers: {
            //     'Authorization': `Bearer ${get().accessToken}`,
            //   },
            // });

            // Clear user context from error monitoring and analytics
            clearSentryUser();
            resetUser();

            set(
              {
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
              },
              false,
              'logout:success'
            );
          } catch (error) {
            console.error('Logout error:', error);
            // Clear state even if API call fails
            clearSentryUser();
            resetUser();
            
            set(
              {
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
              },
              false,
              'logout:error'
            );
          }
        },

        /**
         * Check authentication status
         * 
         * Validates token with backend and refreshes user data
         * Called on app initialization
         */
        checkAuth: async () => {
          const { accessToken } = get();
          
          if (!accessToken) {
            set({ isAuthenticated: false, isLoading: false }, false, 'checkAuth:no-token');
            return;
          }

          set({ isLoading: true }, false, 'checkAuth:start');
          
          try {
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v3';
            let token = accessToken;
            let response = await fetch(`${apiBaseUrl}/auth/me`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (!response.ok) {
              // Token invalid or expired, try to refresh
              await get().refreshAccessToken();
              const newToken = get().accessToken;
              if (!newToken) {
                throw new Error('Token refresh failed');
              }
              // Re-fetch /auth/me with the new token
              response = await fetch(`${apiBaseUrl}/auth/me`, {
                headers: {
                  'Authorization': `Bearer ${newToken}`,
                },
              });
              if (!response.ok) {
                throw new Error('Authentication failed after token refresh');
              }
            }

            const data = await response.json();
            
            set(
              {
                user: data.data,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              },
              false,
              'checkAuth:success'
            );
          } catch (error) {
            set(
              {
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
              },
              false,
              'checkAuth:error'
            );
          }
        },

        /**
         * Refresh access token using refresh token
         */
        refreshAccessToken: async () => {
          const { refreshToken } = get();
          
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          try {
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v3';
            const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (!response.ok) {
              throw new Error('Token refresh failed');
            }

            const data = await response.json();
            
            set(
              {
                accessToken: data.data.access_token,
                isAuthenticated: true,
              },
              false,
              'refreshToken:success'
            );
          } catch (error) {
            // Refresh failed, clear auth state
            set(
              {
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
              },
              false,
              'refreshToken:error'
            );
            throw error;
          }
        },
      }),
      {
        name: 'auth-storage', // localStorage key
        partialize: (state) => ({
          // Persist user data, tokens, and auth status
          // Note: In production, consider using httpOnly cookies for tokens
          user: state.user,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    {
      name: 'AuthStore', // DevTools name
    }
  )
);

/**
 * Selector hooks for optimized re-renders
 * Only subscribes to specific state slices
 */
export const useUser = () => useAuthStore((state) => state.user);
export const useAccessToken = () => useAuthStore((state) => state.accessToken);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useLogin = () => useAuthStore((state) => state.login);
export const useLogout = () => useAuthStore((state) => state.logout);
export const useCheckAuth = () => useAuthStore((state) => state.checkAuth);
export const useRefreshToken = () => useAuthStore((state) => state.refreshAccessToken);
export const useClearAuthError = () => useAuthStore((state) => state.clearError);

/**
 * Utility hook to check if user has specific permission
 */
export const useHasPermission = (permission: string): boolean => {
  const user = useUser();
  if (!user) return false;
  // Global scope (super_admin) has all permissions
  if (user.scope === 'global' || user.role === 'super_admin') return true;
  // Company admin has all company-level permissions
  if (user.role === 'admin' && !permission.startsWith('platform.')) return true;
  return user.permissions?.includes(permission) ?? false;
};

/**
 * Utility hook to check if user has specific role
 */
export const useHasRole = (role: string): boolean => {
  const user = useUser();
  if (!user) return false;
  return user.role === role;
};

/**
 * Utility hook to check if user is super_admin (global scope)
 */
export const useIsSuperAdmin = (): boolean => {
  const user = useUser();
  return user?.scope === 'global' || user?.role === 'super_admin';
};
